/**
 * Promoții — reduceri care se aplică singure, în toate canalele.
 *
 * Ce trebuie să înțeleagă managerul din prima secundă: o promoție nu este un afiș lipit pe meniu,
 * ci o regulă pe care o scrie o singură dată aici, iar platforma o aplică peste prețurile din
 * meniul public, din codurile QR și de pe ecrane. De aceea pagina pune pe primul plan:
 *   • previzualizarea exactă (ce prețuri vor vedea clienții, produs cu produs);
 *   • canalele pe care se aplică (QR / web / ecrane) și programul (zile, ore, perioadă);
 *   • starea reală (activ acum / programată / în pauză), ca să nu existe promoții „uitate".
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Clock24,
  ConfirmDialog,
  DateTime24,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatDateTime, formatNumber, useResource } from '../lib/hooks.js';
import { ROMANIA_CLOCK, fromRomaniaInput, toRomaniaInput } from '../lib/romania-time.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

interface PromotionTarget {
  id?: string;
  targetType: 'all' | 'product' | 'category' | 'menu' | 'menu_section';
  targetId: string | null;
  label: string | null;
}

interface Promotion {
  id: string;
  publicId: string;
  name: string;
  description: string | null;
  type: 'discount_percent' | 'discount_fixed' | 'banner' | 'featured' | 'menu_of_the_day' | 'bundle';
  status: 'draft' | 'scheduled' | 'active' | 'expired' | 'paused';
  discountPercent: number | null;
  discountAmountCents: number | null;
  priceOverrideCents: number | null;
  colorPrimary: string | null;
  badgeText: string | null;
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  timezone: string;
  recurrence: { weekdays?: number[]; from?: string; to?: string } | null;
  showOnQr: boolean;
  showOnDisplay: boolean;
  showOnWeb: boolean;
  targets: PromotionTarget[];
  isLive: boolean;
}

interface Restaurant {
  id: string;
  name: string;
}
interface Location {
  id: string;
  name: string;
  restaurant_id: string;
  is_active: boolean;
}
interface Product {
  id: string;
  name: string;
  price_cents: number;
  category_id: string | null;
  restaurant_id: string;
}
interface Category {
  id: string;
  name: string;
  restaurant_id: string;
}
interface Menu {
  id: string;
  name: string;
  restaurant_id: string;
}

interface PreviewRow {
  name: string;
  currency: string;
  beforeCents: number;
  afterCents: number;
  badge: string | null;
  applies: boolean;
}

const TYPE_LABELS: Record<Promotion['type'], { label: string; hint: string }> = {
  discount_percent: { label: 'Reducere procentuală', hint: 'ex. −20% la produsele alese' },
  discount_fixed: { label: 'Reducere cu sumă fixă', hint: 'ex. 5 lei mai puțin la fiecare produs' },
  menu_of_the_day: { label: 'Meniul zilei', hint: 'un preț unic, valabil astăzi' },
  bundle: { label: 'Pachet / ofertă combinată', hint: 'ex. 2 produse la un preț avantajos' },
  banner: { label: 'Anunț (fără modificare de preț)', hint: 'doar o bandă de informare în meniu' },
  featured: { label: 'Evidențiere', hint: 'produsul apare primul în meniu' },
};

const STATUS: Record<Promotion['status'], { label: string; tone: 'success' | 'warning' | 'neutral' | 'brand' | 'info' }> = {
  active: { label: 'activă', tone: 'success' },
  scheduled: { label: 'programată', tone: 'info' },
  draft: { label: 'ciornă', tone: 'neutral' },
  paused: { label: 'în pauză', tone: 'warning' },
  expired: { label: 'încheiată', tone: 'neutral' },
};

const WEEKDAYS = [
  { value: 1, label: 'L' },
  { value: 2, label: 'M' },
  { value: 3, label: 'M' },
  { value: 4, label: 'J' },
  { value: 5, label: 'V' },
  { value: 6, label: 'S' },
  { value: 0, label: 'D' },
];

const EMPTY_FORM = {
  name: '',
  description: '',
  restaurantId: '',
  locationId: '',
  type: 'discount_percent' as Promotion['type'],
  discountPercent: '20',
  discountAmount: '',
  priceOverride: '',
  badgeText: '',
  colorPrimary: '#0F766E',
  priority: '0',
  startsAt: '',
  endsAt: '',
  weekdays: [] as number[],
  from: '',
  to: '',
  showOnQr: true,
  showOnDisplay: true,
  showOnWeb: true,
  targetType: 'all' as PromotionTarget['targetType'],
  targetIds: [] as string[],
};

function toLocalInput(value: string | null): string {
  return toRomaniaInput(value);
}

export function PromotionsPage() {
  const session = useSession();
  const toast = useToast();
  const canManage = session.permissions.includes('promotion.create');

  const promotions = useResource<{ promotions: Promotion[] }>(() => api.get<{ promotions: Promotion[] }>('/promotions'));
  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'));
  const locations = useResource<Location[]>(() => api.get<Location[]>('/locations'));
  const products = useResource<Product[]>(() => api.get<Product[]>('/products'));
  const categories = useResource<Category[]>(() => api.get<Category[]>('/categories'));
  const menus = useResource<Menu[]>(() => api.get<Menu[]>('/menus'));

  const [statusFilter, setStatusFilter] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [previewFor, setPreviewFor] = useState<Promotion | null>(null);
  const [preview, setPreview] = useState<{ live: boolean; affected: number; products: PreviewRow[] } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleting, setDeleting] = useState<Promotion | null>(null);
  const [pausing, setPausing] = useState<Promotion | null>(null);

  const list = useMemo(() => {
    const rows = promotions.data?.promotions ?? [];
    return statusFilter ? rows.filter((promotion) => promotion.status === statusFilter) : rows;
  }, [promotions.data, statusFilter]);

  const activeCount = (promotions.data?.promotions ?? []).filter((promotion) => promotion.isLive).length;

  function openCreate() {
    setEditing(null);
    setErrors({});
    setForm({ ...EMPTY_FORM, restaurantId: restaurants.data?.[0]?.id ?? '' });
    setEditorOpen(true);
  }

  function openEdit(promotion: Promotion) {
    setEditing(promotion);
    setErrors({});
    setForm({
      name: promotion.name,
      description: promotion.description ?? '',
      restaurantId: '',
      locationId: '',
      type: promotion.type,
      discountPercent: promotion.discountPercent ? String(promotion.discountPercent) : '',
      discountAmount: promotion.discountAmountCents ? String(promotion.discountAmountCents / 100) : '',
      priceOverride: promotion.priceOverrideCents ? String(promotion.priceOverrideCents / 100) : '',
      badgeText: promotion.badgeText ?? '',
      colorPrimary: promotion.colorPrimary ?? '#0F766E',
      priority: String(promotion.priority),
      startsAt: toLocalInput(promotion.startsAt),
      endsAt: toLocalInput(promotion.endsAt),
      weekdays: promotion.recurrence?.weekdays ?? [],
      from: promotion.recurrence?.from ?? '',
      to: promotion.recurrence?.to ?? '',
      showOnQr: promotion.showOnQr,
      showOnDisplay: promotion.showOnDisplay,
      showOnWeb: promotion.showOnWeb,
      targetType: promotion.targets[0]?.targetType ?? 'all',
      targetIds: promotion.targets.map((target) => target.targetId ?? '').filter(Boolean),
    });
    setEditorOpen(true);
  }

  function targetOptions(type: PromotionTarget['targetType']) {
    if (type === 'product') return (products.data ?? []).map((product) => ({ id: product.id, label: `${product.name} — ${(product.price_cents / 100).toFixed(2)} lei` }));
    if (type === 'category') return (categories.data ?? []).map((category) => ({ id: category.id, label: category.name }));
    if (type === 'menu') return (menus.data ?? []).map((menu) => ({ id: menu.id, label: menu.name }));
    return [];
  }

  async function save() {
    setBusy(true);
    setErrors({});
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      type: form.type,
      badgeText: form.badgeText.trim() || null,
      colorPrimary: form.colorPrimary,
      priority: Number(form.priority) || 0,
      startsAt: form.startsAt ? fromRomaniaInput(form.startsAt) : null,
      endsAt: form.endsAt ? fromRomaniaInput(form.endsAt) : null,
      timezone: 'Europe/Bucharest',
      recurrence: form.from && form.to ? { weekdays: form.weekdays.length ? form.weekdays : [0, 1, 2, 3, 4, 5, 6], from: form.from, to: form.to } : null,
      showOnQr: form.showOnQr,
      showOnDisplay: form.showOnDisplay,
      showOnWeb: form.showOnWeb,
      targets:
        form.targetType === 'all'
          ? [{ targetType: 'all', targetId: null }]
          : form.targetIds.map((id) => ({ targetType: form.targetType, targetId: id })),
    };
    if (['discount_percent'].includes(form.type)) payload.discountPercent = Number(form.discountPercent) || null;
    if (form.type === 'discount_fixed') payload.discountAmountCents = Math.round(Number(form.discountAmount) * 100) || null;
    if (['menu_of_the_day', 'bundle'].includes(form.type)) payload.priceOverrideCents = Math.round(Number(form.priceOverride) * 100) || null;

    try {
      if (editing) {
        await api.patch(`/promotions/${editing.id}`, payload);
        toast.success('Promoție actualizată', 'Modificările se aplică la următoarea publicare a meniului.');
      } else {
        await api.post('/promotions', { ...payload, restaurantId: form.restaurantId, locationId: form.locationId || null });
        toast.success('Promoție creată', 'Publică meniul pentru ca prețurile noi să apară la clienți.');
      }
      setEditorOpen(false);
      await promotions.reload();
    } catch (error) {
      const fields = fieldErrors(error);
      setErrors(fields);
      toast.error('Promoția nu a putut fi salvată', Object.values(fields)[0]?.[0] ?? errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus(promotion: Promotion, next: 'active' | 'paused') {
    setBusy(true);
    try {
      await api.post(`/promotions/${promotion.id}/status`, { status: next });
      toast.success(next === 'active' ? 'Promoție activată' : 'Promoție oprită', promotion.name);
      await promotions.reload();
    } catch (error) {
      toast.error('Nu am putut schimba starea', errorMessage(error));
    } finally {
      setBusy(false);
      setPausing(null);
    }
  }

  async function remove() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/promotions/${deleting.id}`);
      toast.success('Promoție ștearsă', `${deleting.name} nu mai apare în meniu.`);
      setDeleting(null);
      await promotions.reload();
    } catch (error) {
      toast.error('Promoția nu a putut fi ștearsă', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function showPreview(promotion: Promotion) {
    setPreviewFor(promotion);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const data = await api.get<{ live: boolean; affected: number; products: PreviewRow[] }>(`/promotions/${promotion.id}/preview`);
      setPreview(data);
    } catch (error) {
      toast.error('Previzualizarea nu a putut fi calculată', errorMessage(error));
    } finally {
      setPreviewLoading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Promoții"
        subtitle={
          activeCount
            ? `${activeCount} ${activeCount === 1 ? 'promoție activă' : 'promoții active'} — prețurile reduse apar automat în meniu, la QR și pe ecrane`
            : 'Reduceri și oferte care se aplică automat în toate canalele, fără editarea prețurilor'
        }
        actions={
          <>
            <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-40" aria-label="Filtru stare">
              <option value="">Toate stările</option>
              {Object.entries(STATUS).map(([value, meta]) => (
                <option key={value} value={value}>
                  {meta.label}
                </option>
              ))}
            </Select>
            {canManage ? (
              <Button icon={<Icon name="plus" />} onClick={openCreate}>
                Promoție nouă
              </Button>
            ) : null}
          </>
        }
      />

      {promotions.error ? (
        <InlineAlert tone="danger" title="Promoțiile nu au putut fi încărcate">
          {promotions.error}{' '}
          <button type="button" className="underline" onClick={() => void promotions.reload()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : null}

      {promotions.loading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} className="flex flex-col gap-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-4 w-32" />
            </Card>
          ))}
        </div>
      ) : list.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {list.map((promotion) => {
            const status = STATUS[promotion.status] ?? STATUS.draft;
            const value =
              promotion.discountPercent !== null
                ? `−${promotion.discountPercent}%`
                : promotion.discountAmountCents !== null
                  ? `−${(promotion.discountAmountCents / 100).toFixed(2)} lei`
                  : promotion.priceOverrideCents !== null
                    ? `${(promotion.priceOverrideCents / 100).toFixed(2)} lei preț fix`
                    : 'fără modificare de preț';
            return (
              <Card key={promotion.id} className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-display text-title text-ink-950">{promotion.name}</h2>
                      <Badge tone={status.tone}>{status.label}</Badge>
                      {promotion.isLive ? (
                        <Badge tone="success">
                          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
                          activă acum
                        </Badge>
                      ) : promotion.status === 'active' ? (
                        // Activă, dar în afara orelor programate: diferența contează pentru manager.
                        <Badge tone="neutral">în afara programului</Badge>
                      ) : null}
                    </div>
                    <p className="text-body-s text-ink-600">{value} · {TYPE_LABELS[promotion.type]?.label ?? promotion.type}</p>
                    {promotion.description ? <p className="text-caption text-ink-400">{promotion.description}</p> : null}
                  </div>
                  {promotion.badgeText ? (
                    <span
                      className="shrink-0 rounded-full px-3 py-1 text-caption font-medium text-white"
                      style={{ backgroundColor: promotion.colorPrimary ?? '#0F766E' }}
                    >
                      {promotion.badgeText}
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-caption text-ink-600">
                  {promotion.targets.map((target) => (
                    <span key={`${target.targetType}-${target.targetId ?? 'all'}`} className="rounded-full border border-ink-200 px-2.5 py-1">
                      {target.targetType === 'all' ? 'toate produsele' : target.label ?? target.targetType}
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5">
                    {promotion.showOnQr ? <Badge tone="neutral">QR</Badge> : null}
                    {promotion.showOnWeb ? <Badge tone="neutral">web</Badge> : null}
                    {promotion.showOnDisplay ? <Badge tone="neutral">ecrane</Badge> : null}
                  </span>
                </div>

                <dl className="grid grid-cols-2 gap-2 text-caption text-ink-600 sm:grid-cols-3">
                  <div className="flex flex-col">
                    <dt className="text-ink-400">Începe</dt>
                    <dd>{promotion.startsAt ? formatDateTime(promotion.startsAt) : 'imediat'}</dd>
                  </div>
                  <div className="flex flex-col">
                    <dt className="text-ink-400">Se încheie</dt>
                    <dd>{promotion.endsAt ? formatDateTime(promotion.endsAt) : 'fără termen'}</dd>
                  </div>
                  <div className="flex flex-col">
                    <dt className="text-ink-400">Program</dt>
                    <dd>
                      {promotion.recurrence?.from && promotion.recurrence?.to
                        ? `${promotion.recurrence.from}–${promotion.recurrence.to}`
                        : 'permanent'}
                    </dd>
                  </div>
                </dl>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" icon={<Icon name="chart" />} onClick={() => void showPreview(promotion)}>
                    Previzualizează prețurile
                  </Button>
                  {canManage ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(promotion)}>
                        Editează
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => (promotion.status === 'active' ? setPausing(promotion) : void toggleStatus(promotion, 'active'))}
                      >
                        {promotion.status === 'active' ? 'Oprește' : 'Activează'}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleting(promotion)}>
                        Șterge
                      </Button>
                    </>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<Icon name="sparkles" size={26} />}
          title={statusFilter ? 'Nicio promoție cu această stare' : 'Nicio promoție încă'}
          description={
            statusFilter
              ? 'Schimbă filtrul sau creează o promoție nouă.'
              : 'Cea mai simplă promoție: −20% la tot meniul, între 15:00 și 18:00. Se aplică automat la QR, pe web și pe ecrane.'
          }
          action={canManage ? <Button icon={<Icon name="plus" />} onClick={openCreate}>Creează prima promoție</Button> : undefined}
        />
      )}

      {/* ─────────────── editor ─────────────── */}
      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? `Editează „${editing.name}"` : 'Promoție nouă'}
        description="Regula scrisă aici se aplică peste prețurile din meniu la publicare — în toate canalele deodată."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>
              Renunță
            </Button>
            <Button loading={busy} onClick={() => void save()}>
              {editing ? 'Salvează modificările' : 'Creează promoția'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nume promoție" required errors={errors.name}>
              {(props) => (
                <Input
                  {...props}
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  placeholder="Happy Hour, Meniul Zilei, 2 la 1 la pizza"
                />
              )}
            </Field>
            <Field label="Tip de promoție" required hint={TYPE_LABELS[form.type]?.hint}>
              {(props) => (
                <Select {...props} value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as Promotion['type'] })}>
                  {Object.entries(TYPE_LABELS)
                    .filter(([code]) => code in { discount_percent: 1, discount_fixed: 1, menu_of_the_day: 1, bundle: 1, banner: 1, featured: 1 })
                    .map(([code, meta]) => (
                      <option key={code} value={code}>
                        {meta.label}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          </div>

          {!editing ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Restaurant" required errors={errors.restaurantId}>
                {(props) => (
                  <Select {...props} value={form.restaurantId} onChange={(event) => setForm({ ...form, restaurantId: event.target.value })}>
                    <option value="">Alege restaurantul…</option>
                    {(restaurants.data ?? []).map((restaurant) => (
                      <option key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Locație (opțional)" hint="Lasă gol pentru toate locațiile restaurantului.">
                {(props) => (
                  <Select {...props} value={form.locationId} onChange={(event) => setForm({ ...form, locationId: event.target.value })}>
                    <option value="">Toate locațiile</option>
                    {(locations.data ?? [])
                      .filter((location) => location.is_active && (!form.restaurantId || location.restaurant_id === form.restaurantId))
                      .map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            {form.type === 'discount_percent' ? (
              <Field label="Procent reducere" required hint="între 1 și 90" errors={errors.discountPercent}>
                {(props) => (
                  <Input {...props} type="number" min={1} max={90} value={form.discountPercent} onChange={(event) => setForm({ ...form, discountPercent: event.target.value })} />
                )}
              </Field>
            ) : null}
            {form.type === 'discount_fixed' ? (
              <Field label="Sumă redusă (lei)" required errors={errors.discountAmountCents}>
                {(props) => <Input {...props} type="number" min={0} step="0.5" value={form.discountAmount} onChange={(event) => setForm({ ...form, discountAmount: event.target.value })} />}
              </Field>
            ) : null}
            {form.type === 'menu_of_the_day' || form.type === 'bundle' ? (
              <Field label="Preț promoțional (lei)" required errors={errors.priceOverrideCents}>
                {(props) => <Input {...props} type="number" min={0} step="0.5" value={form.priceOverride} onChange={(event) => setForm({ ...form, priceOverride: event.target.value })} />}
              </Field>
            ) : null}
            <Field label="Text insignă" hint={'apare pe produs în meniu, ex. „-20% azi”'}>
              {(props) => <Input {...props} maxLength={24} value={form.badgeText} onChange={(event) => setForm({ ...form, badgeText: event.target.value })} />}
            </Field>
            <Field label="Culoare insignă">
              {(props) => (
                <Input
                  {...props}
                  type="color"
                  className="h-11 py-1"
                  value={form.colorPrimary}
                  onChange={(event) => setForm({ ...form, colorPrimary: event.target.value })}
                />
              )}
            </Field>
          </div>

          <Field label="Descriere (opțională)" hint="Textul apare doar în dashboard, pentru echipă.">
            {(props) => <Textarea {...props} rows={2} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />}
          </Field>

          {/* Țintele */}
          <div className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface-2 p-4">
            <p className="text-body-s font-medium text-ink-800">Căror produse li se aplică</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Selectează după">
                {(props) => (
                  <Select
                    {...props}
                    value={form.targetType}
                    onChange={(event) => setForm({ ...form, targetType: event.target.value as PromotionTarget['targetType'], targetIds: [] })}
                  >
                    <option value="all">Toate produsele</option>
                    <option value="category">O categorie</option>
                    <option value="product">Produse anume</option>
                    <option value="menu">Un meniu întreg</option>
                  </Select>
                )}
              </Field>
              {form.targetType === 'all' ? (
                <p className="self-end text-caption text-ink-400">Se aplică tuturor produselor publicate, indiferent de categorie.</p>
              ) : (
                <Field label="Alege (Ctrl/Cmd pentru mai multe)" required>
                  {(props) => (
                    <select
                      {...props}
                      multiple
                      size={4}
                      className="w-full rounded-[var(--radius-md)] border border-ink-200 bg-surface px-3 py-2 text-body-s"
                      value={form.targetIds}
                      onChange={(event) => setForm({ ...form, targetIds: Array.from(event.target.selectedOptions).map((option) => option.value) })}
                    >
                      {targetOptions(form.targetType).map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              )}
            </div>
          </div>

          {/* Program */}
          <div className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface-2 p-4">
            <p className="text-body-s font-medium text-ink-800">Când se aplică · {ROMANIA_CLOCK}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Începe la" hint="00:00–23:59, gol = imediat">
                {(props) => (
                  <DateTime24 id={props.id} value={form.startsAt} onChange={(startsAt) => setForm({ ...form, startsAt })} />
                )}
              </Field>
              <Field label="Se încheie la" hint="00:00–23:59, gol = fără termen">
                {(props) => <DateTime24 id={props.id} value={form.endsAt} onChange={(endsAt) => setForm({ ...form, endsAt })} />}
              </Field>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-body-s text-ink-600">Se repetă în zilele</span>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((day) => {
                  const selected = form.weekdays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        setForm({
                          ...form,
                          weekdays: selected ? form.weekdays.filter((value) => value !== day.value) : [...form.weekdays, day.value],
                        })
                      }
                      className={cx(
                        'h-9 w-9 rounded-full border text-body-s font-medium transition-ui',
                        selected ? 'border-brand-600 bg-brand-100 text-brand-700' : 'border-ink-200 bg-surface text-ink-600 hover:border-ink-400',
                      )}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="De la ora" hint="00:00–23:59, gol = toată ziua">
                {(props) => <Clock24 id={props.id} allowEmpty value={form.from} onChange={(from) => setForm({ ...form, from })} />}
              </Field>
              <Field label="Până la ora" hint="00:00–23:59, poate trece peste miezul nopții (22:00 → 02:00)">
                {(props) => <Clock24 id={props.id} allowEmpty value={form.to} onChange={(to) => setForm({ ...form, to })} />}
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Switch checked={form.showOnQr} onChange={(value) => setForm({ ...form, showOnQr: value })} label="Meniu QR" description="codurile de pe masă" />
              <Switch checked={form.showOnWeb} onChange={(value) => setForm({ ...form, showOnWeb: value })} label="Meniul web" description="link public" />
              <Switch checked={form.showOnDisplay} onChange={(value) => setForm({ ...form, showOnDisplay: value })} label="Ecrane" description="TV-urile din sală" />
            </div>
            <Field label="Prioritate" hint="când două promoții se suprapun, câștigă cea cu prioritate mai mare">
              {(props) => <Input {...props} type="number" min={0} max={100} className="max-w-32" value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })} />}
            </Field>
          </div>

          <InlineAlert tone="info" title="Cum ajung prețurile la clienți">
            După salvare, publică meniul (Meniuri → Publică). Ecranele preiau automat prețurile noi la următoarea
            sincronizare, iar codurile QR nu trebuie regenerate — ele conțin doar adresa meniului.
          </InlineAlert>
        </div>
      </Modal>

      {/* ─────────────── previzualizare ─────────────── */}
      <Modal
        open={previewFor !== null}
        onClose={() => setPreviewFor(null)}
        title={previewFor ? `Ce vor vedea clienții: ${previewFor.name}` : ''}
        description="Calculat cu exact aceleași reguli pe care le folosește publicarea meniului."
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setPreviewFor(null)}>
            Închide
          </Button>
        }
      >
        {previewLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-9" />
            ))}
          </div>
        ) : preview ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone={preview.live ? 'success' : 'warning'}>{preview.live ? 'se aplică acum' : 'nu se aplică în acest moment'}</Badge>
              <span className="text-body-s text-ink-600">
                {formatNumber(preview.affected)} produse afectate din {formatNumber(preview.products.length)}
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full border-collapse text-body-s">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-caption uppercase tracking-wide text-ink-400">
                    <th className="pb-2 font-medium">Produs</th>
                    <th className="pb-2 text-right font-medium">Preț normal</th>
                    <th className="pb-2 text-right font-medium">Preț promoțional</th>
                    <th className="pb-2 text-right font-medium">Insignă</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {preview.products
                    .filter((row) => row.applies)
                    .slice(0, 40)
                    .map((row) => (
                      <tr key={row.name}>
                        <td className="py-2 pr-3 text-ink-950">{row.name}</td>
                        <td className="nums py-2 text-right text-ink-400 line-through">{(row.beforeCents / 100).toFixed(2)}</td>
                        <td className="nums py-2 text-right font-medium text-ink-950">{(row.afterCents / 100).toFixed(2)} {row.currency}</td>
                        <td className="py-2 text-right">
                          {row.badge ? (
                            <span className="rounded-full px-2 py-0.5 text-caption text-white" style={{ backgroundColor: previewFor?.colorPrimary ?? '#0F766E' }}>
                              {row.badge}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {preview.affected === 0 ? (
                <p className="py-4 text-body-s text-ink-600">
                  Nicio reducere nu s-ar aplica. Verifică programul (zile/ore), perioada și produsele alese.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={pausing !== null}
        onClose={() => setPausing(null)}
        onConfirm={() => void (pausing && toggleStatus(pausing, 'paused'))}
        title="Oprești promoția?"
        description={pausing ? `„${pausing.name}" nu se va mai aplica la clienți. Poți reporni oricând — datele rămân.` : ''}
        confirmLabel="Oprește promoția"
        tone="primary"
        loading={busy}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
        title="Ștergi promoția?"
        description={deleting ? `„${deleting.name}" dispare din meniu. Statisticile istorice rămân în rapoarte.` : ''}
        confirmLabel="Șterge promoția"
        loading={busy}
      />
    </div>
  );
}
